import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { di } from 'react-magnetic-di';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Inline, Stack, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ErrorStateImage } from './assets/index.tsx';
import messages from './messages.tsx';

export function ComponentsTableErrorState({ testId }: { testId?: string }) {
	di(window);
	const { formatMessage } = useIntl();

	const reloadButtonTestId = testId && `${testId}-reload-button`;

	const handleReloadClick = useCallback(
		(_: React.MouseEvent | React.KeyboardEvent, analyticEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticEvent, 'compassComponentsTableErrorStateReload');

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.reload();
		},
		[],
	);

	return (
		<Container data-testid={testId}>
			<Inline space="space.400" alignBlock="center">
				<Box xcss={errorStateImageWrapperStyles}>
					<ErrorStateImage />
				</Box>
				<Stack alignInline="start">
					<Heading>{formatMessage(messages.heading)}</Heading>

					<Box xcss={bodyStyles}>{formatMessage(messages.body)}</Box>

					<Button testId={reloadButtonTestId} appearance="primary" onClick={handleReloadClick}>
						{formatMessage(messages.reload)}
					</Button>
				</Stack>
			</Inline>
		</Container>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	width: 600,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.div({
	font: token('font.heading.small'),
	marginBottom: token('space.200'),
});

const bodyStyles = xcss({
	marginBottom: 'space.300',
});

const errorStateImageWrapperStyles = xcss({
	marginBottom: 'space.500',
});
